import { format } from 'date-fns';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { LEDGER_CODE_LOAN_AMOUNT } from 'utils/constants';
import { AccountingType } from 'v2-types/order';

import { updateOrderPolicy } from './graphql/mutations';
import { getOrder, getPolicyTabsData } from './graphql/querys';
import { updateType } from './contexts/policyInfoContext';
import useListings from './services/getlistings';
import { Endorsements, FormPolicy, InsuranceCoverageInfo, Tab } from './services/types';
import { useAccountingUpdateSocketEvent } from "hooks/useAccountingUpdateFromSocket";
import useAlert from 'utils/alert';
import { ACCOUNTING_PURCHASE_PRICE } from 'v2-utils/contants';
import { useTranslation } from 'react-i18next';

const usePolicyRefactor = () => {
  const { id } = useParams<{ id: string }>();
  const showAlert = useAlert();

  const [showNoDataFoundMessage, setShowNoDataFoundMessage] = useState<boolean>(false);
  const [tabs, setTabs] = useState<Tab[]>([]);
  const [selectedTab, setSelectedTab] = useState<Tab | null>(null);
  const selectedTabIdRef = useRef<string>('');
  const [insuranceInfo, setInsuranceInfo] = useState<InsuranceCoverageInfo | null>(null);
  const { t } = useTranslation();

  const [accounting, setAccounting] = useState<AccountingType[]>([]);
  const options = useListings();
  const [typeTransaction, setTypeTransaction] = useState('');
  const [endorsements, setEndorsements] = useState<Endorsements>({
    lendersEndorsements: [],
    ownersEndorsements: [],
  });
  const queryCache = useQueryClient();
  useAccountingUpdateSocketEvent({
    callback: () => queryCache.invalidateQueries(['policy-order', id])
  });

  const [info, setInfo] = useState<updateType>({
    time: '',
    type: 'info',
    message: 'listen updates',
  });
  const methods = useForm<FormPolicy>({
    mode: 'onChange',
    defaultValues: {
      parties: [],
      owners_policy_tab: {
        disclosure_section: 'B',
        endorsements_shown_as: '',
        policy_type: '',
        proposed_insured: '',
        rate_type: '',
        remove_section_13: false,
        stat_code: '',
      },
      lenders_policy_tab: {
        disclosure_section: 'B',
        endorsements_shown_as: '',
        policy_type: '',
        proposed_insured: '',
        rate_type: '',
        remove_section_13: false,
        stat_code: '',
      },
    },
  });

  const policyData = useQuery(['policy-order', id], () => getOrder(id), {
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      const purchasePriceEntry = data.getOrder.accounting?.find(
        (entry) => entry.code === ACCOUNTING_PURCHASE_PRICE
      );
      const loanAmountEntry = data.getOrder.accounting?.find(
        (entry) => entry.code === LEDGER_CODE_LOAN_AMOUNT
      );
      const insuranceParties = data.getOrder.parties;

      const buyers = insuranceParties
        .filter((party) => party.kinds.some((kind) => kind === 'Buyer'))
        .map((buyer) => ({ id: buyer._id, name: buyer.name }));
      const lenders = insuranceParties
        .filter((party) => party.kinds.some((kind) => kind === 'Lender'))
        .map((lender) => ({ id: lender._id, name: lender.name }));

      if (purchasePriceEntry || loanAmountEntry) {
        setInsuranceInfo({
          purchaseAmount: purchasePriceEntry?.amount ?? 0,
          loanAmount: loanAmountEntry?.amount ?? 0,
          buyers,
          lenders,
        });
      }
      setAccounting(data?.getOrder?.accounting || []);
      const { lenders_policy, owners_policy, parties, configuration } = data.getOrder;

      methods.reset({
        parties: parties || [],
        owners_policy_tab: owners_policy,
        lenders_policy_tab: lenders_policy,
      });
      setTypeTransaction(configuration.transaction_type);
      setEndorsements({
        lendersEndorsements: data.getOrder.lenders_policy.endorsements ?? [],
        ownersEndorsements: data.getOrder.owners_policy.endorsements ?? [],
      });
    },
  });

  const { mutate: updateOrderInfo, ...updateOrderResponse } = useMutation(updateOrderPolicy, {
    onSuccess: () => {
      updateOrderResponse.reset();
      const now = format(new Date(), 'hh:mm');
      setInfo({ time: now, type: 'success', message: `updated at  ${now}` });
      queryCache.invalidateQueries(['policy-order', id]);
    },
  });

  useEffect(() => {
    if (policyData.isFetching) {
      showAlert('Getting new information', 'info');
    }
  }, [policyData.isFetching, showAlert]);

  const { isLoading: isPolicyTabsLoading } = useQuery(
    ['policy-tabs'],
    () => getPolicyTabsData(id),
    {
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response.getOrder.accounting.length === 0) {
          setShowNoDataFoundMessage(true);
          return;
        }

        const accountingData = response.getOrder.accounting;
        const tabList: Tab[] = [];
        if (
          accountingData.filter(
            (entry) => entry.code === ACCOUNTING_PURCHASE_PRICE && entry.amount > 0
          ).length
        ) {
          tabList.push({
            id: 'owners_policy_tab',
            description: t('policyInfoAndRates:owners-policy'),
          });
        }

        if (
          accountingData.filter(
            (entry) => entry.code === LEDGER_CODE_LOAN_AMOUNT && entry.amount > 0
          ).length
        ) {
          tabList.push({
            id: 'lenders_policy_tab',
            description: t('policyInfoAndRates:lenders-policy'),
          });
        }

        if (tabList.length) {
          setSelectedTab(tabList[0]);
          selectedTabIdRef.current = tabList[0].id;
        }

        if (tabList.length === 0) setShowNoDataFoundMessage(true);

        setTabs(tabList);
      },
    }
  );

  const handleSelectTab = (tab: Tab) => {
    setSelectedTab(tab);
    selectedTabIdRef.current = tab.id;
  };

  return {
    selectedTabIdRef,
    handleSelectTab,
    insuranceInfo,
    showNoDataFoundMessage,
    isPolicyTabsLoading,
    tabs,
    selectedTab,
    loadingData: policyData.isLoading,
    accounting,
    methods,
    options,
    setAccounting,
    info,
    setInfo,
    updateOrderInfo,
    id,
    typeTransaction,
    endorsements,
    setEndorsements,
  };
};

export default usePolicyRefactor;
